import authApi from './index';
import { convertRecordToMilliseconds } from 'utils/time';

// CONTROL SAFE_MODE
// Set SAFE_MODE to true to enable safe mode, where API requests are logged but not sent.
// Set to false for normal operation where API requests are actually sent.
const SAFE_MODE = false;

/**
 * Fetch the result highlights for a specific club by sending a GET request to the '/results/get-result-highlights' endpoint.
 * It takes the club ID and the number of highlights to fetch (default is 8).
 * @deprecated
 * @param {string} clubId - The ID of the club whose result highlights are to be fetched.
 * @param {number} numberOfHighlights - The number of result highlights to fetch (default is 8).
 * @returns {Promise} - The response from the API containing the result highlights.
 */
export const getResultHighlights = async (clubId, numberOfHighlights = 8) => {
  return await authApi.get(
    `/results/get-result-highlights?clubId=${clubId}&numberOfHighlights=${numberOfHighlights}`
  );
};

/**
 * Fetches aggregate event results.
 *
 * This function retrieves aggregated results for a specific club, result, event type, age group, gender, and round type.
 *
 * @param {string} clubID - The unique identifier of the club.
 * @param {string} meetID - The unique identifier of the result.
 * @param {string} eventType - The type of the event (e.g., "100m", "Relay").
 * @param {string} ageGroup - The age group for the event (e.g., "U14").
 * @param {string} gender - The gender for the event (e.g., "Male", "Female").
 * @param {string} roundType - The round type for the event (e.g., "Heat", "Final").
 * @returns {Promise<Object>} - A Promise that resolves to the API response containing the event results.
 */
export const getAggResultsForEventType = async (
  clubID,
  meetID,
  eventType,
  ageGroup,
  gender,
  roundType
) => {
  const requestData = {
    clubID,
    meetID,
    eventType,
    ageGroup,
    gender,
    roundType,
  };

  return await authApi.post(`/events/get-agg-event-results`, requestData);
};

/**
 * Fetches details of selected events for a specific club and result.
 *
 * This function retrieves information about specific events for a given club and result.
 * Optionally, it filters the results based on a `lastUpdated` timestamp.
 *
 * @param {string} clubId - The unique identifier of the club.
 * @param {string} meetId - The unique identifier of the result.
 * @param {Array<string>} eventIds - An array of event IDs to fetch details for.
 * @param {string|null} lastUpdated - An optional timestamp to filter events by their last update date (format: YYYY-MM-DDTHH:mm:ss).
 * @returns {Promise<Object>} - A Promise that resolves to the API response containing details of the selected events.
 */
export const getSelectedEvents = async (
  clubId,
  meetId,
  eventIds,
  lastUpdated = null
) => {
  return await authApi.post(`/results/get-selected-events`, {
    clubId,
    meetId,
    eventIds,
    lastUpdated,
  });
};

/**
 * TRACK EVENTS
 *
 * Updates the event results for a specific track timing aggregate event in the database.
 *
 * This function sends a PUT request to update multiple timing results in an aggregated track timing event.
 * Must only be used for track timing events (e.g., 100m, 1500m) where results have time measurements.
 * @param {string} eventID - The unique identifier for the event being updated.
 * @param {Array} results - The updated time results for the event.
 * @returns {Promise} A Promise resolving to the API response.
 */
export const updateResults = async (eventID, results) => {
  const requestData = {
    eventID,
    results,
  };

  if (SAFE_MODE) {
    console.log('🔄 API CALL: updateResults');
    console.log('📋 Request Data:', JSON.stringify(requestData, null, 2));
    console.log('🛡️ SAFE_MODE: Skipping actual API call');
    return {
      data: {
        message: 'SAFE_MODE: updateResults logged but not sent',
        requestData,
        success: true,
      },
    };
  }

  // call the API when not in safe mode
  return await authApi.put(`/results/edit-event-results`, requestData);
};

/**
 * FIELD EVENTS
 *
 * Using the new edit-event-attempts API for to update attempt results for field events.
 *
 * This function sends a PUT request to update multiple attempts in an aggregated field event.
 * Must only be used for field events (e.g., throws, jumps) where attempts have distance measurements.
 *
 * @param {Object} params - The parameters for updating attempts
 * @param {string} params.eventID - The unique identifier for the event being updated
 * @param {Array} params.attempts - Array of attempt objects to update
 * @returns {Promise} A Promise resolving to the API response
 */
export const editEventAttempts = async ({ eventID, eventType, attempts }) => {
  const requestData = {
    eventID,
    attempts,
  };

  if (SAFE_MODE) {
    console.log('🔄 API CALL: editEventAttempts');
    console.log('📋 Request Data:', JSON.stringify(requestData, null, 2));
    console.log('🛡️ SAFE_MODE: Skipping actual API call');
    return {
      data: {
        message: 'SAFE_MODE: editEventAttempts logged but not sent',
        requestData,
        success: true,
      },
    };
  }
  // call the API when not in safe mode
  try {
    const response = await authApi.put(
      '/results/edit-event-attempts',
      requestData
    );
    console.log('API response:', response);
    return response;
  } catch (error) {
    console.error('API error:', error);
    console.error('Error response:', error.response);
    throw error;
  }
};

/**
 * HIGH JUMP EVENTS
 *
 * Using the new edit-high-jump-results API for to update highJump results for field events.
 *
 * This function sends a PUT request to update multiple attempts in an aggregated field event.
 * Must only be used for field events (e.g., throws, jumps) where attempts have distance measurements.
 *
 * @param {Object} params - The parameters for updating attempts
 * @param {string} params.eventID - The unique identifier for the event being updated
 * @param {Array} params.attempts - Array of attempt objects {
 *    "resultID": "string",
      "height": 0,
      "attempt1": "string",
      "attempt2": "string",
      "attempt3": "string"}[]
 * @returns {Promise} A Promise resolving to the API response
 */
export const editHighJumpResults = async ({ eventID, attempts }) => {
  const requestData = {
    eventID,
    attempts,
  };

  if (SAFE_MODE) {
    console.log('🔄 API CALL: editHighJumpResults');
    console.log('📋 Request Data:', JSON.stringify(requestData, null, 2));
    console.log('🛡️ SAFE_MODE: Skipping actual API call');
    return {
      data: {
        message: 'SAFE_MODE: editHighJumpResults logged but not sent',
        requestData,
        success: true,
      },
    };
  }

  // Call the API when not in safe mode
  try {
    const response = await authApi.put(
      '/results/edit-high-jump-results',
      requestData
    );
    return response;
  } catch (error) {
    console.error('API error:', error);
    console.error('Error response:', error.response);
    throw error;
  }
};

/**
 * Fetches aggregated events based on their publishing status.
 *
 * This function retrieves events for a specific club and result, filtered by their publishing status.
 * The events are aggregated by event type and agegroup. For exaxmple, if there are multiple Heats for a single event,
 * they will be aggregated into a single event.
 *
 * @param {string} clubID - The unique identifier of the club.
 * @param {string} meetID - The unique identifier of the result.
 * @param {string} publishingStatus - The publishing status of the events (e.g., "ClubMembers", "AdminOnly").
 * @returns {Promise<Object>} - A Promise that resolves to the API response containing the filtered events.
 */
export const getAggEventsByStatus = async (
  clubID,
  meetID,
  publishingStatus
) => {
  return await authApi.post(`/events/get-agg-events`, {
    clubID,
    meetID,
    publishingStatus,
  });
};

/**
 * Updates the status of specific events.
 *
 * This function sends a request to update the statuses of a list of events.
 *
 * @param {Array<Object>} events - An array of event objects, each containing event details and the updated status.
 * @returns {Promise<Object>} - A Promise that resolves to the API response after updating the event statuses.
 */
export const updateEventStatus = async (events) => {
  return await authApi.put(`/results/event-status`, events);
};

/**
 * @deprecated
 * Updates the publishing status of an event.
 *
 * @param {string} eventID - The unique identifier of the event to be updated.
 * @param {string} newPublishingStatus - The new publishing status to set for the event.
 * @returns {Promise} - A promise that resolves to the response of the API call.
 */
export const updateEventPublishingStatus = async (
  eventID,
  newPublishingStatus
) => {
  return await authApi.put(`/events/update-event-pub-status`, {
    eventID,
    newPublishingStatus,
  });
};

/**
 * Updates the publishing status of an event.
 *
 * @param {string} eventID - The unique identifier of the event to be updated.
 * @param {string} newPublishingStatus - The new publishing status to set for the event.
 * @returns {Promise} - A promise that resolves to the response of the API call.
 */
export const updateEventPublishingStatusWithMeetID = async (
  clubID,
  meetID,
  newPublishingStatus
) => {
  return await authApi.put(`/events/update-event-pub-status`, {
    clubID,
    meetID,
    newPublishingStatus,
  });
};

/**
 * Fetches high jump results for a specific event, club, and participant group.
 *
 * This function sends a POST request to retrieve high jump results for a specified club,
 * result, event type, age group, gender, and round type. The results are returned
 * as a promise containing the API response.
 * @deprecated
 * @async
 * @function getHighJumpResults
 *
 * @param {string} clubID - The unique identifier of the club.
 * @param {string} meetID - The unique identifier of the result.
 * @param {string} eventType - The type of event (e.g., "High Jump").
 * @param {string} ageGroup - The age group of participants (e.g., "U18", 15).
 * @param {string} gender - The gender category (e.g., "M", "F").
 * @param {string} roundType - The round type of the event (e.g., "final", "qualifier").
 *
 * @returns {Promise<Object>} The API response containing high jump results.
 *
 * @throws {Error} Will throw an error if the API request fails or returns an error response.
 *
 * @example
 * // Example usage
 * const results = await getHighJumpResults(
 *   "123e4567-e89b-12d3-a456-426614174000",
 *   "456e7890-e12b-34c5-d678-426614174111",
 *   "High Jump",
 *   "U18",
 *   "Boys",
 *   "Heat"
 * );
 * console.log(results);
 *
 * @endpoint POST /events/get-high-jump-results
 *
 * @requestBody
 * {
 *   "clubID": "123e4567-e89b-12d3-a456-426614174000",
 *   "meetID": "456e7890-e12b-34c5-d678-426614174111",
 *   "eventType": "High Jump",
 *   "ageGroup": "U18",
 *   "gender": "Boys",
 *   "roundType": "Heat"
 * }
 *
 * @responseBody
 * {
 *   "status": "success",
 *   "data": [
 *     {
 *     "athleteId": "string",
 *     "firstName": "string",
 *     "lastName": "string",
 *     "athleteNumber": 0,
 *     "age": 0,
 *     "gender": "string",
 *     "position": 0,
 *     "bestClearedHeight": 0,
 *     "competitionStatus": "string",
 *     "isPR": true,
 *     "lastAttemptTime": "2025-01-15T02:48:21.702Z",
 *     "heightAttempts": [
 *       {
 *         "resultId": "string",
 *         "height": 0,
 *         "attempt1": "string",
 *         "attempt2": "string",
 *         "attempt3": "string",
 *         "timestamp": "2025-01-15T02:48:21.702Z",
 *         "hasValidClear": true
 *       }
 *     ]
 *   }
 *   ]
 * }
 *
 * @notes
 * - The `authApi` must be correctly configured with authorization headers.
 * - Ensure that the input parameters match the required formats for the API.
 * - The response may include attempts and results for each athlete, along with their positions.
 */
export const getHighJumpResults = async (
  clubID,
  meetID,
  eventType,
  ageGroup,
  gender,
  roundType
) => {
  const res = await authApi.post(`/meet-events/get-high-jump-results`, {
    clubID,
    meetID,
    eventType,
    ageGroup,
    gender,
    roundType,
  });
  return res?.data;
};

export const exportResultsToFile = async (
  clubID,
  meetID,
  format = 'sf_csv',
  eventIds = undefined,
  lastUpdated = undefined
) => {
  const res = await authApi.post(
    `/ResultsExport/export`,
    {
      clubID,
      meetID,
      format,
      eventIds,
      lastUpdated,
    },
    {
      responseType: 'blob', // Ensure the response is treated as a Blob so we can handle diffieren file types
    }
  );
  return res; // Return the response (with headers, status, etc.) so we can extract the filename
};

export const publishResults = async (newPublishingStatus) => {
  const res = await authApi.put(`/events/agg-event-status`, {
    newPublishingStatus,
  });
  return res?.data;
};

export const getResultsByRound = async (
  clubID,
  meetID,
  eventType,
  ageGroup,
  gender,
  roundType = undefined
) => {
  const res = await authApi.post(`/meet-age-results/get-event-age-results`, {
    clubID,
    meetID,
    eventType,
    ageGroup,
    gender,
    roundType,
  });
  return res?.data;
};

export const getAgeResultsByRound = async (
  clubID,
  meetID,
  eventType,
  ageGroup,
  gender,
  roundType = undefined
) => {
  const res = await authApi.post(`/meet-age-results/get-event-age-results`, {
    clubID,
    meetID,
    eventType,
    ageGroup,
    gender,
    roundType,
  });
  return res?.data;
};

export const getHighJumpAgeResultsByRound = async (
  clubID,
  meetID,
  eventType,
  ageGroup,
  gender,
  roundType = undefined
) => {
  const res = await authApi.post(
    `/meet-age-results/get-high-jump-age-results`,
    {
      clubID,
      meetID,
      eventType,
      ageGroup,
      gender,
      roundType,
    }
  );
  return res?.data;
};

export const getResultsByEventId = async (clubID, meetID, eventID) => {
  const res = await authApi.post(`/meet-events/get-event-results`, {
    clubID,
    meetID,
    eventID,
  });
  return res?.data;
};

/**
 *
 * @deprecated
 */
export const getResultsForAllRound = async (
  clubID,
  meetID,
  eventType,
  ageGroup,
  gender,
  roundType
) => {
  const res = await authApi.post(`/meet-events/get-event-results`, {
    clubID,
    meetID,
    eventType,
    ageGroup,
    gender,
    roundType,
  });
  return res?.data;
};

export const getHighJumpResultsByEventId = async (clubID, meetID, eventID) => {
  const res = await authApi.post(`/meet-events/get-high-jump-results`, {
    clubID,
    meetID,
    eventID,
  });
  return res?.data;
};

// Bundled endpoints — return metadata + results in one payload.
// One call per page; sibling tabs and round types come along in metadata.

export const getAgeView = async ({
  clubID,
  meetID,
  eventType,
  athleteAgeGroup,
  gender,
  roundType,
}) => {
  const res = await authApi.post('/meet-age-results/get-age-view', {
    clubID,
    meetID,
    eventType,
    athleteAgeGroup,
    gender,
    roundType,
  });
  return res?.data;
};

export const getHighJumpAgeView = async ({
  clubID,
  meetID,
  eventType,
  athleteAgeGroup,
  gender,
  roundType,
}) => {
  const res = await authApi.post('/meet-age-results/get-high-jump-age-view', {
    clubID,
    meetID,
    eventType,
    athleteAgeGroup,
    gender,
    roundType,
  });
  return res?.data;
};

export const getEventRoundView = async ({ clubID, meetID, eventID }) => {
  const res = await authApi.post('/meet-events/get-event-round-view', {
    clubID,
    meetID,
    eventID,
  });
  return res?.data;
};

// A relay round's Overall tab: every team of the round's races by its aggregate
// position (utils/relayTeams.js). Asked for with any race of the round.
export const getRelayOverall = async ({ clubID, meetID, eventID }) => {
  const res = await authApi.post('/meet-events/get-relay-overall', {
    clubID,
    meetID,
    eventID,
  });
  return res?.data;
};

export const getHighJumpRoundView = async ({ clubID, meetID, eventID }) => {
  const res = await authApi.post('/meet-events/get-high-jump-round-view', {
    clubID,
    meetID,
    eventID,
  });
  return res?.data;
};

// Handles creating multiple new track results for an existing event.
export const createTrackResultsBatch = async (
  clubID,
  meetID,
  eventID,
  resultType,
  results
) => {
  const parseDistanceFromEventType = (eventType) => {
    if (!eventType) return null;

    // Handle formats like "100m", "1500m", "5000m", etc.
    const match = eventType.match(/^(\d+(?:\.\d+)?)m?$/i);
    if (match) {
      return parseInt(match[1], 10);
    }

    // Handle relay formats like "4x100m" -> 400, and "4x50m Shuttle" -> 200
    const relayMatch = eventType.match(/^(\d+)x(\d+)m?(?: shuttle)?$/i);
    if (relayMatch) {
      return parseInt(relayMatch[1], 10) * parseInt(relayMatch[2], 10);
    }

    return 0;
  };

  // Convert frontend result format to ResultDto format expected by API
  const resultDtos = results.map((result) => {
    // Parse distance from event type
    const distance = parseDistanceFromEventType(resultType);

    return {
      resultID: '', // Let API generate
      eventID,
      athleteID: result.athleteID || '',
      athleteFirstName: result.athleteFirstName || '',
      athleteLastName: result.athleteLastName || '',
      athleteNumber: result.athleteNumber || 0,
      athleteAge: result.athleteAge || 0,
      athleteGender: result.athleteGender || '',
      // A relay team's house (a school); the server decides who owns the result.
      teamID: result.teamID || null,
      resultType: 'Finish',
      lapNumber: result.lapNumber || 0,
      attemptNumber: result.attemptNumber || 0,
      distance: distance,
      velocity: result.velocity || 0,
      laneNumber: result.laneNumber || 0,
      resultValue: convertRecordToMilliseconds(result.resultValue).toString(),
      resultRawValue: convertRecordToMilliseconds(
        result.resultValue
      ).toString(),
      unit: 'ms',
      status: result.status,
      timestamp: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isDeleted: 0,
    };
  });

  const reqBody = {
    eventID,
    clubID,
    meetID,
    results: resultDtos,
  };

  if (SAFE_MODE) {
    console.log('🟢 API CALL: createTrackResultsBatch');
    console.log('📋 Request Data:', JSON.stringify(reqBody, null, 2));
    console.log('🛡️ SAFE_MODE: Skipping actual API call');
    return {
      data: {
        message: 'SAFE_MODE: createTrackResultsBatch logged but not sent',
        requestData: reqBody,
        success: true,
        createdCount: results.length,
      },
    };
  }

  const res = await authApi.post(
    `/Results/create-results-for-existing-event`,
    reqBody
  );
  return res?.data;
};

// Handles creating multiple new field results for an existing event.
export const createFieldResultsBatch = async (
  clubID,
  meetID,
  eventID,
  resultDtos
) => {
  const reqBody = {
    eventID,
    clubID,
    meetID,
    results: resultDtos,
  };

  if (SAFE_MODE) {
    console.log('🟢 API CALL: createFieldResultsBatch');
    console.log('📋 Request Data:', JSON.stringify(reqBody, null, 2));
    console.log('🛡️ SAFE_MODE: Skipping actual API call');
    return {
      data: {
        message: 'SAFE_MODE: createFieldResultsBatch logged but not sent',
        requestData: reqBody,
        success: true,
        createdCount: resultDtos.length,
      },
    };
  }

  const res = await authApi.post(
    `/Results/create-results-for-existing-event`,
    reqBody
  );
  return res?.data;
};

// Handles creating multiple new high jump event results for an existing event.
export const createHighJumpResultsBatch = async (
  clubID,
  meetID,
  eventID,
  resultDtos
) => {
  const reqBody = {
    clubID,
    meetID,
    eventID,
    results: resultDtos,
  };

  if (SAFE_MODE) {
    console.log('🟢 API CALL: createHighJumpResultsBatch');
    console.log('📋 Request Data:', JSON.stringify(reqBody, null, 2));
    console.log('🛡️ SAFE_MODE: Skipping actual API call');
    return {
      data: {
        message: 'SAFE_MODE: createHighJumpResultsBatch logged but not sent',
        requestData: reqBody,
        success: true,
        createdCount: resultDtos.length,
      },
    };
  }

  const res = await authApi.post(
    `/Results/create-results-for-existing-event`,
    reqBody
  );
  return res?.data;
};

/**
 * Deletes one or more results using the batch delete API
 *
 * @param {string|string[]} resultIds - Single result ID or array of result IDs to delete
 * @param {string} clubID - The club identifier
 * @returns {Promise<Object>} - A Promise that resolves to the API response
 */
export const deleteResults = async (resultIds, clubID) => {
  try {
    // Ensure resultIds is always an array
    const resultIdsArray = Array.isArray(resultIds) ? resultIds : [resultIds];

    const requestData = {
      clubID,
      resultIds: resultIdsArray,
    };

    if (SAFE_MODE) {
      console.log('🗑️ API CALL: deleteResults');
      console.log('📋 Request Data:', JSON.stringify(requestData, null, 2));
      console.log('🛡️ SAFE_MODE: Skipping actual API call');
      return {
        data: {
          Message: 'SAFE_MODE: deleteResults logged but not executed',
          deletedCount: resultIdsArray.length,
          success: true,
          requestData,
        },
      };
    }
    // Call the API when not in safe mode
    const response = await authApi.delete('/results/delete-results', {
      data: requestData,
    });

    return response;
  } catch (error) {
    //console.error('Delete results API error:', error);
    //console.error('Error response:', error.response);
    throw error;
  }
};

/**
 * Convenience function for deleting a single result
 *
 * @param {string} resultID - The unique identifier of the result to delete
 * @param {string} clubID - The club identifier
 * @returns {Promise<Object>} - A Promise that resolves to the API response
 */
export const deleteResult = async (resultID, clubID) => {
  return deleteResults(resultID, clubID);
};

/**
 * Fetches training event summaries for a meet.
 */
export const getTrainingResultsMeetSummary = async (clubId, meetId) => {
  const res = await authApi.post(`/training-results/meet-summary`, {
    clubId,
    meetId,
  });
  return res?.data;
};

/**
 * Fetches detailed training results for specific event IDs.
 * Optionally filters to a single athlete when athleteId is provided.
 */
export const getTrainingResults = async (
  clubId,
  meetId,
  eventIds,
  athleteId = null
) => {
  const body = { clubId, meetId, eventIds };
  if (athleteId) body.athleteId = athleteId;
  const res = await authApi.post(
    `/training-results/get-training-results`,
    body
  );
  return res?.data;
};

/**
 * Exports training results for a meet as a CSV file.
 */
export const exportTrainingResultsToFile = async (payload) => {
  return authApi.post('/training-results/export-csv', payload, {
    responseType: 'blob',
  });
};

/**
 * Fetches training event summaries across all meets for a single athlete.
 * seasonId is optional: null = the club's current season.
 */
export const getAthleteTrainingMeetSummaries = async (
  clubId,
  athleteId,
  seasonId = null
) => {
  const res = await authApi.post(
    `/training-results/athlete-meet-summaries`,
    {
      clubId,
      athleteId,
      seasonId,
    }
  );
  return res?.data;
};

export const createResultsForExistingEvent = async (
  clubID,
  meetID,
  eventID,
  results
) => {
  const requestData = {
    clubID,
    meetID,
    eventID,
    results,
  };

  if (SAFE_MODE) {
    console.log('🟢 API CALL: createResultsForExistingEvent');
    console.log('📋 Request Data:', JSON.stringify(requestData, null, 2));
    console.log('🛡️ SAFE_MODE: Skipping actual API call');
    return {
      data: {
        message: 'SAFE_MODE: createResultsForExistingEvent logged but not sent',
        requestData,
        success: true,
        createdCount: results.length,
      },
    };
  }

  return await authApi.post(
    `/results/create-results-for-existing-event`,
    requestData
  );
};

/**
 * Fetches the results sync queue status for a meet: per-status job counts plus
 * detail rows for every job not yet completed (including failures with their
 * server error messages). Admin-only on the server — only call for admin users.
 * See docs/results-sync-queue-design.md in the portal repo.
 * @param {string} meetId - The ID of the meet.
 * @returns {Promise} - { queued, processing, completed, failed, jobs: [...] }
 */
export const getResultSyncStatus = async (meetId) => {
  return await authApi.get(`/results/sync-status/${meetId}`);
};

/**
 * Fetches club-wide results sync activity for the Results Sync report: every meet
 * with sync jobs still in the retention window (completed kept 7 days, failed 30),
 * most recent activity first. Admin-only on the server.
 * See docs/results-sync-queue-design.md in the portal repo.
 * @param {string} clubId - The ID of the club.
 * @returns {Promise} - { clubID, meets: [{ meetID, meetName, meetDate, lastActivityAt, queued, processing, completed, failed, jobs: [...] }] }
 */
export const getClubResultSyncStatus = async (clubId) => {
  return await authApi.get(`/results/sync-status/club/${clubId}`);
};

/**
 * Requeues a Failed results sync job for immediate reprocessing (admin retry).
 * @param {string} syncJobId - The ID of the failed sync job.
 * @returns {Promise} - Confirmation message with the job's new status.
 */
export const retryResultSyncJob = async (syncJobId) => {
  return await authApi.post(`/results/sync-jobs/${syncJobId}/retry`);
};

// A relay team's runners (utils/relayTeams.js): one athlete a leg, in leg
// order; an empty list clears them. Answers the runners as saved.
export const saveRelayRunners = async (resultID, runners) => {
  const res = await authApi.put(`/results/${resultID}/runners`, { runners });
  return res?.data;
};
